import { useState } from 'react';
import Modal from './Modal';
import { PersonMultiPicker } from './PersonPicker';
import { assigneePersonsOf } from '../lib/personRefs';
import type { AppSettings, NamedRecord, PersonOption, Task } from '../types';

const MAX_DIRECT_ASSIGNEES = 5;

export interface TaskDraft {
  title: string;
  description: string;
  districtId: string;
  committee: string;
  status: string;
  dueDate: string | null;
  assignmentMode: 'position' | 'people' | 'none';
  assignedPosition: string | null;
  /** Direct assignees are PEOPLE (canonical); the server derives the legacy Contact list. */
  assignedPersonIds: string[];
}

/** The goal or initiative a task is filed under — just what the form needs. */
export interface TaskFormContext {
  title: string;
  districtId: string;
  committee: string;
}

export default function TaskForm({
  context,
  initial,
  settings,
  people,
  labelFor,
  toPerson = () => null,
  onAddPerson,
  districts,
  onClose,
  onSave,
}: {
  context: TaskFormContext;
  initial: Task | null;
  settings: AppSettings;
  people: PersonOption[];
  labelFor: (personId: string) => string | null;
  /** Maps a legacy Contact id to its Person (for a Task that predates the Person field). */
  toPerson?: (contactId: string) => string | null;
  onAddPerson?: () => void;
  districts: NamedRecord[];
  onClose: () => void;
  onSave: (draft: TaskDraft) => Promise<void>;
}) {
  const [d, setD] = useState<TaskDraft>(() => ({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    districtId: initial?.districtId ?? districts[0]?.id ?? context.districtId,
    committee: initial?.committee ?? context.committee,
    status: initial?.status ?? settings.taskStatuses[0]?.id ?? '',
    dueDate: initial?.dueDate ?? null,
    assignmentMode: initial && assigneePersonsOf(initial, toPerson).length
      ? 'people'
      : initial?.assignedPosition
        ? 'position'
        : 'none',
    assignedPosition: initial?.assignedPosition ?? null,
    assignedPersonIds: initial ? assigneePersonsOf(initial, toPerson) : [],
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof TaskDraft>(k: K, v: TaskDraft[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));

  const pickPosition = (name: string) => {
    setD((prev) => ({
      ...prev,
      assignedPosition: name || null,
    }));
  };

  const districtName = districts.find((x) => x.id === d.districtId)?.name ?? '';

  return (
    <Modal title={initial ? 'Edit Task' : 'Add Task'} onClose={onClose}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
        For: <b>{context.title}</b>
      </div>

      <label>Title</label>
      <input value={d.title} onChange={(e) => set('title', e.target.value)} />

      <label>Description</label>
      <textarea rows={2} value={d.description} onChange={(e) => set('description', e.target.value)} />

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>District</label>
          <select value={d.districtId} disabled>
            {districts.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label>Committee</label>
          <select value={d.committee} disabled>
            {settings.committees.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>Status</label>
          <select value={d.status} onChange={(e) => set('status', e.target.value)}>
            {settings.taskStatuses.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label>Due date</label>
          <input
            type="date"
            value={d.dueDate ?? ''}
            onChange={(e) => set('dueDate', e.target.value || null)}
          />
        </div>
      </div>

      <label>Assignment</label>
      <select
        value={d.assignmentMode}
        onChange={(e) => set('assignmentMode', e.target.value as TaskDraft['assignmentMode'])}
      >
        <option value="none">Unassigned</option>
        <option value="position">By position (turnover-proof)</option>
        <option value="people">Directly to person(s)</option>
      </select>

      {d.assignmentMode === 'position' && (
        <>
          <label>Position</label>
          <select
            value={d.assignedPosition ?? ''}
            onChange={(e) => pickPosition(e.target.value)}
          >
            <option value="">— select —</option>
            {settings.positions.map((p) => (
              <option key={p.name}>{p.name}</option>
            ))}
          </select>
        </>
      )}

      {d.assignmentMode === 'people' && (
        <>
          <label>People {districtName && `(${districtName})`}</label>
          <PersonMultiPicker
            options={people}
            districtId={d.districtId}
            values={d.assignedPersonIds}
            max={MAX_DIRECT_ASSIGNEES}
            onChange={(ids) => set('assignedPersonIds', ids)}
            labelFor={labelFor}
            onAddPerson={onAddPerson}
          />
        </>
      )}

      {err && <div className="err">{err}</div>}

      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={busy || !d.title.trim()}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave(d);
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Could not save that Task.');
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
      </div>
    </Modal>
  );
}
